import { getBackendUrl } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';
import { router } from '../../lib/router.js';

import '../deploy-channels/deploy-channels-view.js';
import '../alert-channels/alert-channels-view.js';
import '../issue-channels/issue-channels-view.js';
import '../issue-firehose/issue-firehose-view.js';
import '../signups-channel/signups-channel-view.js';
import '../social-feed/social-feed-view.js';
import '../issue-broadcast-channels/issue-broadcast-channels-view.js';
import '../channel-apps/channel-apps-view.js';
import '../channel-products/channel-products-view.js';
import '../apps/apps-view.js';

const NAV_GROUPS = [
    {
        label: 'Admin',
        icon: 'bi-gear',
        items: [
            { id: 'deploy-channels', label: 'Deploy Channels', path: '/deploy-channels' },
            { id: 'alert-channels', label: 'Alert Channels', path: '/alert-channels' },
            { id: 'issue-channels', label: 'Issue Channels', path: '/issue-channels' },
            { id: 'issue-firehose', label: 'Issue Firehose', path: '/issue-firehose' },
            { id: 'signups-channel', label: 'Signups Channel', path: '/signups-channel' },
            { id: 'social-feed', label: 'Social Media', path: '/social-feed' },
            {
                id: 'issue-broadcast-channels',
                label: 'Issue Broadcasts',
                path: '/issue-broadcast-channels',
            },
            { id: 'apps', label: 'Apps', path: '/apps' },
            { id: 'channel-apps', label: 'Channel Apps', path: '/channel-apps' },
            { id: 'channel-products', label: 'Channel Products', path: '/channel-products' },
        ],
    },
];

class D3poApp extends BaseComponent {
    static properties = {
        _loading: { type: Boolean, state: true },
        _authenticated: { type: Boolean, state: true },
        _user: { type: Object, state: true },
        _activeTab: { type: String, state: true },
        _error: { type: String, state: true },
        _showUserMenu: { type: Boolean, state: true },
    };

    static styles = css`
        :host {
            display: block;
            width: 100%;
            min-height: 100vh;
            background-color: #f8f9fa;
        }
        .login-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            gap: 1rem;
        }
        .user-menu { position: relative; }
        .user-menu-toggle {
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.25rem 0.5rem;
            border-radius: 0.375rem;
            border: 1px solid transparent;
            color: #ccc;
        }
        .user-menu-toggle:hover { border-color: #555; }
        .user-dropdown {
            position: absolute;
            right: 0;
            top: 100%;
            margin-top: 0.25rem;
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            box-shadow: 0 0.25rem 0.5rem rgba(0,0,0,0.1);
            min-width: 10rem;
            z-index: 1000;
        }
        .user-dropdown button {
            display: block;
            width: 100%;
            padding: 0.5rem 1rem;
            text-align: left;
            text-decoration: none;
            color: #333;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 0.875rem;
        }
        .user-dropdown button:hover { background-color: #f8f9fa; }
    `;

    constructor() {
        super();
        this._loading = true;
        this._authenticated = false;
        this._user = null;
        this._activeTab = 'deploy-channels';
        this._error = null;
        this._showUserMenu = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._onDocClick = (e) => {
            if (
                this._showUserMenu &&
                !e.composedPath().some((el) => el.classList?.contains('user-menu'))
            ) {
                this._showUserMenu = false;
            }
        };
        document.addEventListener('click', this._onDocClick);
        this._unsubscribeAuth = authState.subscribe((state) => {
            const wasLoading = this._loading;
            this._loading = state.loading;
            this._authenticated = state.isAuthenticated;
            this._user = state.user;
            if (wasLoading && !state.loading && state.isAuthenticated) {
                router.handleRoute();
            }
        });
        this._unsubscribeRouter = router.subscribe((state) => {
            this._activeTab =
                state.route === 'home' ? 'deploy-channels' : state.route || 'deploy-channels';
        });
        this._initAuth();
        router.handleRoute();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocClick);
        if (this._unsubscribeAuth) this._unsubscribeAuth();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _initAuth() {
        try {
            // Use the explicit backend URL — d3po's SPA and API are on
            // different origins (Bun dev server vs Go locally, CloudFront vs ALB
            // in dev/prd) with no proxy, so a relative path resolves against the
            // SPA host and falls through to index.html.
            const response = await fetch(`${getBackendUrl()}/api/config`);
            if (!response.ok) throw new Error('Failed to fetch config');
            const config = await response.json();
            await authState.init({ ...config, appName: 'd3po' });

            // Don't auto-login from /auth/callback or when an OAuth response
            // is in flight — init() owns those paths. Otherwise a failure in
            // init() would re-trigger login and loop endlessly.
            const params = new URLSearchParams(window.location.search);
            const inOAuthFlow =
                window.location.pathname === '/auth/callback' ||
                params.has('code') ||
                params.has('error');
            if (!authState.isAuthenticated && window.location.pathname !== '/' && !inOAuthFlow) {
                await authState.login();
            }
        } catch (err) {
            console.error('Auth init failed:', err);
            this._error = err.message;
            this._loading = false;
        }
    }

    async _handleLogin() {
        await authState.login();
    }
    async _handleLogout() {
        this._showUserMenu = false;
        await authState.logout();
    }

    render() {
        if (this._loading) {
            return html`
                <div class="login-container">
                    <div class="spinner-border text-primary" role="status"></div>
                    <p class="text-muted">Loading...</p>
                </div>
            `;
        }

        if (this._error) {
            return html`
                <div class="login-container">
                    <h4>D3-PO</h4>
                    <div class="alert alert-danger">${this._error}</div>
                </div>
            `;
        }

        if (!this._authenticated) {
            return html`
                <div class="login-container">
                    <h4>D3-PO</h4>
                    <p class="text-muted">Discord bot administration</p>
                    <button class="btn btn-primary" @click="${this._handleLogin}">Sign In</button>
                </div>
            `;
        }

        return html`
            <nav class="navbar navbar-dark bg-dark">
                <div class="container-fluid d-flex justify-content-between align-items-center">
                    <span class="navbar-brand mb-0">D3-PO</span>
                    <div class="user-menu">
                        <div class="user-menu-toggle" @click="${() => (this._showUserMenu = !this._showUserMenu)}">
                            <i class="bi bi-person-circle" style="font-size: 1.25rem;"></i>
                            <span class="d-none d-md-inline" style="font-size: 0.875rem;">${this._user?.name || this._user?.email || ''}</span>
                            <i class="bi bi-chevron-down" style="font-size: 0.65rem;"></i>
                        </div>
                        ${
                            this._showUserMenu
                                ? html`
                            <div class="user-dropdown">
                                <button @click="${this._handleLogout}">Sign Out</button>
                            </div>
                        `
                                : ''
                        }
                    </div>
                </div>
            </nav>

            <div class="d-flex" style="min-height: calc(100vh - 56px);">
                <nav class="bg-white border-end pt-3" style="width: 220px; flex-shrink: 0;">
                    ${NAV_GROUPS.map(
                        (group) => html`
                        <div class="mb-1">
                            <div class="d-flex align-items-center gap-2 px-3 py-2 text-dark" style="font-size: 0.9rem; font-weight: 500;">
                                <i class="bi ${group.icon}" style="font-size: 1rem; color: #666;"></i>
                                <span class="flex-grow-1">${group.label}</span>
                            </div>
                            <div class="ms-4">
                                ${group.items.map(
                                    (item) => html`
                                    <a class="d-block px-3 py-1 text-decoration-none ${this._activeTab === item.id ? 'text-primary fw-medium' : 'text-secondary'}"
                                       style="font-size: 0.85rem;"
                                       href="${item.path}">
                                        ${item.label}
                                    </a>
                                `,
                                )}
                            </div>
                        </div>
                    `,
                    )}
                </nav>

                <main class="flex-grow-1 d-flex justify-content-center p-4" style="background: #fff;">
                    <div style="width: 100%; max-width: 1320px;">
                        ${this._activeTab === 'deploy-channels' ? html`<deploy-channels-view></deploy-channels-view>` : ''}
                        ${this._activeTab === 'alert-channels' ? html`<alert-channels-view></alert-channels-view>` : ''}
                        ${this._activeTab === 'issue-channels' ? html`<issue-channels-view></issue-channels-view>` : ''}
                        ${this._activeTab === 'issue-firehose' ? html`<issue-firehose-view></issue-firehose-view>` : ''}
                        ${this._activeTab === 'signups-channel' ? html`<signups-channel-view></signups-channel-view>` : ''}
                        ${this._activeTab === 'social-feed' ? html`<social-feed-view></social-feed-view>` : ''}
                        ${this._activeTab === 'issue-broadcast-channels' ? html`<issue-broadcast-channels-view></issue-broadcast-channels-view>` : ''}
                        ${this._activeTab === 'apps' ? html`<apps-view></apps-view>` : ''}
                        ${this._activeTab === 'channel-apps' ? html`<channel-apps-view></channel-apps-view>` : ''}
                        ${this._activeTab === 'channel-products' ? html`<channel-products-view></channel-products-view>` : ''}
                    </div>
                </main>
            </div>
        `;
    }
}

customElements.define('d3po-app', D3poApp);
